import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Swiper.css";

const Swiper = ({
    children,
    gap = 16,
    drag = true,
    showArrows = false,
    showDots = true,
    ariaLabel = "Content carousel",
}) => {
    const trackRef = useRef(null);
    const slideRefs = useRef([]);
    const dragStateRef = useRef({
        pointerId: null,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        startIndex: 0,
        axis: null,
        isRtl: false,
    });
    const suppressClickRef = useRef(false);

    const [isDragging, setIsDragging] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const childrenArray = React.Children.toArray(children);
    const slideCount = childrenArray.length;

    const getClosestSlideIndex = useCallback(() => {
        const track = trackRef.current;
        if (!track || !slideRefs.current.length) return 0;

        const trackRect = track.getBoundingClientRect();
        const isRtl = window.getComputedStyle(track).direction === "rtl";
        const trackStart = isRtl ? trackRect.right : trackRect.left;
        let closestIndex = 0;
        let smallestDistance = Infinity;

        slideRefs.current.forEach((slide, index) => {
            if (!slide) return;

            const slideRect = slide.getBoundingClientRect();
            const slideStart = isRtl ? slideRect.right : slideRect.left;
            const distance = Math.abs(slideStart - trackStart);

            if (distance < smallestDistance) {
                smallestDistance = distance;
                closestIndex = index;
            }
        });

        return closestIndex;
    }, []);

    const goToSlide = useCallback(
        (index) => {
            const track = trackRef.current;
            if (!track || slideCount === 0) return;

            const safeIndex = Math.max(0, Math.min(index, slideCount - 1));
            const slide = slideRefs.current[safeIndex];
            if (!slide) return;

            const reduceMotion = window
                .matchMedia?.("(prefers-reduced-motion: reduce)")
                .matches;

            slide.scrollIntoView({
                behavior: reduceMotion ? "auto" : "smooth",
                block: "nearest",
                inline: "start",
            });

            setActiveIndex(safeIndex);
        },
        [slideCount]
    );

    const handlePointerDown = (event) => {
        const track = trackRef.current;

        if (
            !drag ||
            !track ||
            (event.pointerType === "mouse" && event.button !== 0)
        ) {
            return;
        }

        suppressClickRef.current = false;
        dragStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: track.scrollLeft,
            startIndex: getClosestSlideIndex(),
            axis: null,
            isRtl: window.getComputedStyle(track).direction === "rtl",
        };

        track.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event) => {
        const track = trackRef.current;
        const dragState = dragStateRef.current;

        if (
            !track ||
            dragState.pointerId === null ||
            dragState.pointerId !== event.pointerId
        ) {
            return;
        }

        const distanceX = event.clientX - dragState.startX;
        const distanceY = event.clientY - dragState.startY;

        if (dragState.axis === null) {
            if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 6) {
                return;
            }

            dragState.axis =
                Math.abs(distanceX) > Math.abs(distanceY)
                    ? "horizontal"
                    : "vertical";

            if (dragState.axis === "horizontal") {
                setIsDragging(true);
            }
        }

        if (dragState.axis !== "horizontal") {
            return;
        }

        event.preventDefault();

        track.scrollLeft = dragState.startScrollLeft - distanceX;
    };

    const finishPointerDrag = (event) => {
        const track = trackRef.current;
        const dragState = dragStateRef.current;

        if (
            !track ||
            dragState.pointerId === null ||
            dragState.pointerId !== event.pointerId
        ) {
            return;
        }

        const distance = event.clientX - dragState.startX;
        const wasHorizontalDrag = dragState.axis === "horizontal";
        const swipeThreshold = 40;
        let targetIndex = getClosestSlideIndex();

        if (wasHorizontalDrag && Math.abs(distance) >= swipeThreshold) {
            const movedToNextSlide = dragState.isRtl
                ? distance > 0
                : distance < 0;

            targetIndex =
                dragState.startIndex + (movedToNextSlide ? 1 : -1);
        }

        track.releasePointerCapture?.(event.pointerId);
        dragStateRef.current.pointerId = null;
        dragStateRef.current.axis = null;
        setIsDragging(false);

        if (wasHorizontalDrag) {
            suppressClickRef.current = Math.abs(distance) >= 6;
            goToSlide(targetIndex);
        }
    };

    const cancelPointerDrag = (event) => {
        const track = trackRef.current;
        const dragState = dragStateRef.current;

        if (
            !track ||
            dragState.pointerId === null ||
            dragState.pointerId !== event.pointerId
        ) {
            return;
        }

        track.releasePointerCapture?.(event.pointerId);
        dragStateRef.current.pointerId = null;
        dragStateRef.current.axis = null;
        setIsDragging(false);
    };

    const handleClickCapture = (event) => {
        if (!suppressClickRef.current) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        suppressClickRef.current = false;
    };

    const handleWheel = useCallback((event) => {
        const track = trackRef.current;
        if (!track) return;

        const isHorizontalGesture =
            Math.abs(event.deltaX) > Math.abs(event.deltaY);
        const horizontalDelta = isHorizontalGesture
            ? event.deltaX
            : event.shiftKey
              ? event.deltaY
              : 0;

        if (horizontalDelta === 0) {
            return;
        }

        event.preventDefault();

        const isRtl = window.getComputedStyle(track).direction === "rtl";
        track.scrollLeft += isRtl ? -horizontalDelta : horizontalDelta;
    }, []);

    const updateActiveSlide = useCallback(() => {
        setActiveIndex(getClosestSlideIndex());
    }, [getClosestSlideIndex]);

    useEffect(() => {
        slideRefs.current = slideRefs.current.slice(0, slideCount);
        setActiveIndex((currentIndex) =>
            Math.min(currentIndex, Math.max(slideCount - 1, 0))
        );

        updateActiveSlide();
        window.addEventListener("resize", updateActiveSlide);

        return () => {
            window.removeEventListener("resize", updateActiveSlide);
        };
    }, [slideCount, updateActiveSlide]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        track.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            track.removeEventListener("wheel", handleWheel);
        };
    }, [handleWheel]);

    const handleKeyDown = (event) => {
        const track = trackRef.current;
        const isRtl =
            track && window.getComputedStyle(track).direction === "rtl";
        let targetIndex = null;

        if (event.key === "ArrowLeft") {
            targetIndex = activeIndex + (isRtl ? 1 : -1);
        } else if (event.key === "ArrowRight") {
            targetIndex = activeIndex + (isRtl ? -1 : 1);
        } else if (event.key === "Home") {
            targetIndex = 0;
        } else if (event.key === "End") {
            targetIndex = slideCount - 1;
        }

        if (targetIndex !== null) {
            event.preventDefault();
            goToSlide(targetIndex);
        }
    };

    const scrollPrev = () => goToSlide(activeIndex - 1);
    const scrollNext = () => goToSlide(activeIndex + 1);

    if (slideCount === 0) {
        return null;
    }

    return (
        <div
            className="swiper"
            role="region"
            aria-roledescription="carousel"
            aria-label={ariaLabel}
        >
            {showArrows && (
                <button
                    className="swiper-arrow swiper-arrow--previous"
                    onClick={scrollPrev}
                    type="button"
                    aria-label="Previous slide"
                    disabled={activeIndex === 0}
                >
                    <span className="swiper-arrow__icon" aria-hidden="true">
                        ‹
                    </span>
                </button>
            )}

            <div
                ref={trackRef}
                className={`swiper-track ${isDragging ? "dragging" : ""}`}
                style={{ gap: `${gap}px` }}
                tabIndex={0}
                onScroll={updateActiveSlide}
                onKeyDown={handleKeyDown}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={finishPointerDrag}
                onPointerCancel={cancelPointerDrag}
                onClickCapture={handleClickCapture}
                onDragStart={(event) => event.preventDefault()}
            >
                {childrenArray.map((child, index) => (
                    <div
                        className="swiper-slide"
                        key={index}
                        ref={(element) => {
                            slideRefs.current[index] = element;
                        }}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`${index + 1} of ${slideCount}`}
                    >
                        {child}
                    </div>
                ))}
            </div>

            {showArrows && (
                <button
                    className="swiper-arrow swiper-arrow--next"
                    onClick={scrollNext}
                    type="button"
                    aria-label="Next slide"
                    disabled={activeIndex === slideCount - 1}
                >
                    <span className="swiper-arrow__icon" aria-hidden="true">
                        ›
                    </span>
                </button>
            )}

            {showDots && slideCount > 1 && (
                <div className="swiper-dots">
                    {childrenArray.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`swiper-dot ${
                                activeIndex === index ? "active" : ""
                            }`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={
                                activeIndex === index ? "true" : undefined
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Swiper;
